import React, { Suspense, useEffect } from "react";
import {
  createBrowserRouter,
  RouterProvider,
  Navigate,
  Outlet,
  useLocation,
  useParams,
  useRouteError,
} from "react-router-dom";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { Toaster } from "react-hot-toast";
import { useTranslation } from "react-i18next";
import { useAuthStore } from "./store/authStore";
import ErrorBoundary from "./components/common/ErrorBoundary";
import PlanLimitListener from "./components/common/PlanLimitListener";
import { useIdleSession } from "./hooks/useIdleSession";
import AppLayout from "./components/common/AppLayout";
import LoginPage from "./components/auth/LoginPage";
import RegisterPage from "./components/auth/RegisterPage";
import LandingPage from "./components/landing/LandingPage";
import DashboardPage from "./components/dashboard/DashboardPage";
import ComposerPage from "./components/composer/ComposerPage";
import CalendarPage from "./components/calendar/CalendarPage";
const SubmissionsInboxPage = React.lazy(() => import("./components/submissions/SubmissionsInboxPage"));
// PageBuilderPage & TemplateGalleryPage lazy-loaded inside BuilderLayout
import BuilderLayout from "./components/builder/BuilderLayout";
import AnalyticsPage from "./components/analytics/AnalyticsPage";
import ReportsPage from "./components/analytics/ReportsPage";
import SharedReportPage from "./components/analytics/SharedReportPage";
import ShortLinksPage from "./components/shortener/ShortLinksPage";
import TeamPage from "./components/team/TeamPage";
import ActivityLogPage from "./components/team/ActivityLogPage";
import SettingsPage from "./components/settings/SettingsPage";
import SocialAccountsPage from "./components/settings/SocialAccountsPage";
import { WhiteLabelPage } from "./components/settings/WhiteLabelPage";
import PostsPage from "./components/posts/PostsPage";
import PublicPage from "./components/builder/PublicPage";
import BulkSchedulePage from "./components/bulk/BulkSchedulePage";
import RSSFeedsPage from "./components/rss/RSSFeedsPage";
import ImageEditorPage from "./components/editor/ImageEditorPage";
import VideoEditorPage from "./components/video-editor/VideoEditorPage";
import MediaLibraryPage from "./components/media/MediaLibraryPage";
import PublicMediaSubmissionPage from "./components/media/PublicMediaSubmissionPage";
// DraftsPage removed — drafts are now a tab within PostsPage
import QueuePage from "./components/queue/QueuePage";
import ApprovalsPage from "./components/approvals/ApprovalsPage";
import BillingPage from "./components/billing/BillingPage";
import AutomationsPage from "./components/automations/AutomationsPage";
import WorkflowBuilderPage from "./components/automations/WorkflowBuilderPage";
import UnifiedInboxPage from "./components/inbox/UnifiedInboxPage";
import MarketplacePage from "./components/marketplace/MarketplacePage";
import TemplateManagerPage from "./components/templates/TemplateManager";
import TemplateComposerPage from "./components/templates/TemplateComposer";
import TemplateEditorPage from "./components/templates/TemplateEditorPage";
// LiveBlocksManager now handled inside BuilderLayout
const AGUIPostCreation = React.lazy(() => import("./components/agui/PostCreationChat"));
const AGUIAgentChat = React.lazy(() => import("./components/agui/AgentChat"));
const AGUIDojoChat = React.lazy(() => import("./components/agui/AGUIDojoChat"));
const AGUIProvider = React.lazy(() => import("./components/agui/AGUIProvider"));
import OrchestratorPage from "./components/orchestrator/OrchestratorPage";
import HelpCenterPage from "./components/help/HelpCenterPage";
import WorkspacePage from "./components/workspace/WorkspacePage";
import OnboardingWizard from "./components/onboarding/OnboardingWizard";
// P3-3 — admin ops page for the publish dead-letter queue. Lazy-loaded
// because most users never navigate here.
const DeadLetterQueuePage = React.lazy(
  () => import("./components/ops/DeadLetterQueuePage")
);
// Dev-only diagnostic view — improvement #12. Lazy so it never ships in the
// default bundle for end users.
const DevToolCoverageCard = React.lazy(
  () => import("./components/dev/DevToolCoverageCard")
);
import TermsOfServicePage from "./components/legal/TermsOfServicePage";
import PrivacyPolicyPage from "./components/legal/PrivacyPolicyPage";

// Public marketing pages — code-split so the landing bundle stays lean.
const MktProductComposer = React.lazy(() => import("./components/marketing/pages/ProductComposerPage"));
const MktProductScheduler = React.lazy(() => import("./components/marketing/pages/ProductSchedulerPage"));
const MktProductAnalytics = React.lazy(() => import("./components/marketing/pages/ProductAnalyticsPage"));
const MktProductLinkInBio = React.lazy(() => import("./components/marketing/pages/ProductLinkInBioPage"));
const MktProductInbox = React.lazy(() => import("./components/marketing/pages/ProductInboxPage"));
const MktProductApprovals = React.lazy(() => import("./components/marketing/pages/ProductApprovalsPage"));
const MktProductAutomations = React.lazy(() => import("./components/marketing/pages/ProductAutomationsPage"));
const MktProductListening = React.lazy(() => import("./components/marketing/pages/ProductListeningPage"));
const MktAIStudio = React.lazy(() => import("./components/marketing/pages/AIStudioPage"));
const MktAIImages = React.lazy(() => import("./components/marketing/pages/AIImagesPage"));
const MktAIAgent = React.lazy(() => import("./components/marketing/pages/AIAgentPage"));
const MktAIRepurpose = React.lazy(() => import("./components/marketing/pages/AIRepurposePage"));
const MktPricing = React.lazy(() => import("./components/marketing/pages/PricingPage"));
const MktSolutionAgencies = React.lazy(() => import("./components/marketing/pages/SolutionAgenciesPage"));
const MktSolutionCreators = React.lazy(() => import("./components/marketing/pages/SolutionCreatorsPage"));
const MktSolutionEnterprise = React.lazy(() => import("./components/marketing/pages/SolutionEnterprisePage"));
const MktSolutionSmb = React.lazy(() => import("./components/marketing/pages/SolutionSmallBusinessPage"));
const MktSolutionEcommerce = React.lazy(() => import("./components/marketing/pages/SolutionEcommercePage"));
const MktPlatformsIndex = React.lazy(() => import("./components/marketing/pages/PlatformsIndexPage"));
const MktPlatformPage = React.lazy(() => import("./components/marketing/pages/PlatformPage"));
const MktCompareIndex = React.lazy(() => import("./components/marketing/pages/CompareIndexPage"));
const MktComparePage = React.lazy(() => import("./components/marketing/pages/ComparePage"));
const MktResourcesBlog = React.lazy(() => import("./components/marketing/pages/ResourcesBlogPage"));
const MktResourcesFaq = React.lazy(() => import("./components/marketing/pages/ResourcesFaqPage"));

function MarketingSuspense({ children }: { children: React.ReactNode }) {
  return (
    <Suspense
      fallback={
        <div className="flex items-center justify-center min-h-screen">
          <div className="agui-shimmer h-4 w-32 rounded-md" />
        </div>
      }
    >
      {children}
    </Suspense>
  );
}

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      staleTime: 60_000,
    },
  },
});

/**
 * Layout components for the data router. Each renders an `<Outlet />`
 * for its children routes once auth/onboarding gates pass, or redirects
 * otherwise. This is the direct translation of the legacy
 * ProtectedRoute / PublicOnlyRoute / OnboardingRoute wrapper pattern
 * into the data-router shape.
 */
function ProtectedLayout() {
  const { isAuthenticated, isLoading, user } = useAuthStore();
  if (isLoading) {
    return <div className="flex items-center justify-center h-screen">Loading...</div>;
  }
  if (!isAuthenticated) {
    return <Navigate to="/" replace />;
  }
  if (user && user.onboarding_completed === false) {
    return <Navigate to="/onboarding" replace />;
  }
  return (
    <AppLayout>
      <Outlet />
    </AppLayout>
  );
}

function OnboardingLayout() {
  const { isAuthenticated, isLoading, user } = useAuthStore();
  if (isLoading) {
    return <div className="flex items-center justify-center h-screen">Loading...</div>;
  }
  if (!isAuthenticated) {
    return <Navigate to="/" replace />;
  }
  if (user && user.onboarding_completed !== false) {
    return <Navigate to="/dashboard" replace />;
  }
  return <Outlet />;
}

function PublicOnlyLayout() {
  const { isAuthenticated, isLoading, user } = useAuthStore();
  if (isLoading) {
    return <div className="flex items-center justify-center h-screen">Loading...</div>;
  }
  if (isAuthenticated) {
    if (user && user.onboarding_completed === false) {
      return <Navigate to="/onboarding" replace />;
    }
    return <Navigate to="/dashboard" replace />;
  }
  return <Outlet />;
}

function RootLayout() {
  // PlanLimitListener must live inside the Router so it can useNavigate.
  return (
    <>
      <PlanLimitListener />
      <Outlet />
    </>
  );
}

const lazyShimmer = (
  <div className="flex items-center justify-center h-full">
    <div className="agui-shimmer h-4 w-32 rounded-md" />
  </div>
);

function LegacyPostRouteRedirect() {
  const { postId } = useParams<{ postId?: string }>();
  return <Navigate to={postId ? `/compose/${postId}` : "/posts"} replace />;
}

function AgentModeRedirect({ mode }: { mode: string }) {
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  params.set("mode", mode);
  return <Navigate to={`/agent?${params.toString()}`} replace />;
}

function AppRouteError() {
  const { t } = useTranslation();
  const error = useRouteError() as { status?: number; statusText?: string; message?: string };
  const isNotFound = error?.status === 404;

  return (
    <div className="grid min-h-screen place-items-center bg-[radial-gradient(circle_at_top_left,#ecfdf5_0,#fbfaf7_40%,#f8fafc_100%)] px-6">
      <div className="w-full max-w-lg rounded-[28px] border border-white/80 bg-white/90 p-8 text-center shadow-sm backdrop-blur">
        <div className="mx-auto mb-4 grid h-12 w-12 place-items-center rounded-2xl bg-emerald-50 text-emerald-700">
          {isNotFound ? "404" : "!"}
        </div>
        <h1 className="text-2xl font-semibold tracking-tight text-slate-950">
          {isNotFound
            ? t("route_not_found_title", "Page not found")
            : t("route_error_title", "Something went wrong")}
        </h1>
        <p className="mt-3 text-sm leading-6 text-slate-500">
          {isNotFound
            ? t("route_not_found_desc", "This link may point to an old page or a deleted item.")
            : t("route_error_desc", "The app could not open this page. Try going back to your workspace.")}
        </p>
        <div className="mt-6 flex flex-wrap justify-center gap-3">
          <a
            href="/posts"
            className="inline-flex items-center justify-center rounded-xl bg-emerald-600 px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-emerald-700"
          >
            {t("route_error_posts", "Go to Posts")}
          </a>
          <a
            href="/dashboard"
            className="inline-flex items-center justify-center rounded-xl border border-slate-200 bg-white px-4 py-2 text-sm font-semibold text-slate-700 hover:border-emerald-200 hover:bg-emerald-50"
          >
            {t("route_error_dashboard", "Dashboard")}
          </a>
        </div>
      </div>
    </div>
  );
}

const router = createBrowserRouter([
  {
    element: <RootLayout />,
    errorElement: <AppRouteError />,
    children: [
      // Public Landing (gated: signed-in users bounce to dashboard)
      {
        element: <PublicOnlyLayout />,
        children: [
          { path: "/", element: <LandingPage /> },
          { path: "/login", element: <LoginPage /> },
          { path: "/register", element: <RegisterPage /> },
        ],
      },
      // Fully public (no auth gate)
      { path: "/p/:slug", element: <PublicPage /> },
      { path: "/report/:token", element: <SharedReportPage /> },
      { path: "/terms", element: <TermsOfServicePage /> },
      { path: "/privacy", element: <PrivacyPolicyPage /> },
      { path: "/media/submit", element: <PublicMediaSubmissionPage /> },

      // Marketing — lazy, always-public
      { path: "/product/composer", element: <MarketingSuspense><MktProductComposer /></MarketingSuspense> },
      { path: "/product/scheduler", element: <MarketingSuspense><MktProductScheduler /></MarketingSuspense> },
      { path: "/product/analytics", element: <MarketingSuspense><MktProductAnalytics /></MarketingSuspense> },
      { path: "/product/link-in-bio", element: <MarketingSuspense><MktProductLinkInBio /></MarketingSuspense> },
      { path: "/product/inbox", element: <MarketingSuspense><MktProductInbox /></MarketingSuspense> },
      { path: "/product/approvals", element: <MarketingSuspense><MktProductApprovals /></MarketingSuspense> },
      { path: "/product/automations", element: <MarketingSuspense><MktProductAutomations /></MarketingSuspense> },
      { path: "/product/listening", element: <MarketingSuspense><MktProductListening /></MarketingSuspense> },
      { path: "/ai/studio", element: <MarketingSuspense><MktAIStudio /></MarketingSuspense> },
      { path: "/ai/images", element: <MarketingSuspense><MktAIImages /></MarketingSuspense> },
      { path: "/ai/agent", element: <MarketingSuspense><MktAIAgent /></MarketingSuspense> },
      { path: "/ai/repurpose", element: <MarketingSuspense><MktAIRepurpose /></MarketingSuspense> },
      { path: "/pricing", element: <MarketingSuspense><MktPricing /></MarketingSuspense> },
      { path: "/solutions/agencies", element: <MarketingSuspense><MktSolutionAgencies /></MarketingSuspense> },
      { path: "/solutions/creators", element: <MarketingSuspense><MktSolutionCreators /></MarketingSuspense> },
      { path: "/solutions/enterprise", element: <MarketingSuspense><MktSolutionEnterprise /></MarketingSuspense> },
      { path: "/solutions/small-business", element: <MarketingSuspense><MktSolutionSmb /></MarketingSuspense> },
      { path: "/solutions/ecommerce", element: <MarketingSuspense><MktSolutionEcommerce /></MarketingSuspense> },
      { path: "/platforms", element: <MarketingSuspense><MktPlatformsIndex /></MarketingSuspense> },
      { path: "/platforms/:slug", element: <MarketingSuspense><MktPlatformPage /></MarketingSuspense> },
      { path: "/compare", element: <MarketingSuspense><MktCompareIndex /></MarketingSuspense> },
      { path: "/compare/:slug", element: <MarketingSuspense><MktComparePage /></MarketingSuspense> },
      { path: "/resources/blog", element: <MarketingSuspense><MktResourcesBlog /></MarketingSuspense> },
      { path: "/resources/faq", element: <MarketingSuspense><MktResourcesFaq /></MarketingSuspense> },

      // Onboarding — authed but no onboarding-completed gate.
      {
        element: <OnboardingLayout />,
        children: [{ path: "/onboarding", element: <OnboardingWizard /> }],
      },

      // Authenticated app — wraps every child in AppLayout via the layout.
      {
        element: <ProtectedLayout />,
        children: [
          { path: "/dashboard", element: <DashboardPage /> },
          { path: "/posts", element: <PostsPage /> },
          { path: "/posts/:postId", element: <LegacyPostRouteRedirect /> },
          { path: "/compose", element: <ComposerPage /> },
          { path: "/compose/:postId", element: <ComposerPage /> },
          { path: "/calendar", element: <CalendarPage /> },
          { path: "/builder/*", element: <BuilderLayout /> },
          {
            path: "/pages/:pageId/submissions",
            element: (
              <Suspense fallback={<div className="flex-1 flex items-center justify-center"><div className="agui-shimmer h-4 w-32 rounded-md" /></div>}>
                <SubmissionsInboxPage />
              </Suspense>
            ),
          },
          { path: "/analytics", element: <AnalyticsPage /> },
          { path: "/reports", element: <ReportsPage /> },
          { path: "/links", element: <ShortLinksPage /> },
          { path: "/team", element: <TeamPage /> },
          { path: "/activity", element: <ActivityLogPage /> },
          { path: "/settings", element: <SettingsPage /> },
          { path: "/settings/white-label", element: <WhiteLabelPage /> },
          { path: "/accounts", element: <SocialAccountsPage /> },
          { path: "/bulk", element: <BulkSchedulePage /> },
          { path: "/rss", element: <RSSFeedsPage /> },
          { path: "/editor", element: <ImageEditorPage /> },
          { path: "/video-editor", element: <VideoEditorPage /> },
          { path: "/media", element: <MediaLibraryPage /> },
          { path: "/drafts", element: <Navigate to="/posts" replace /> },
          { path: "/queue", element: <QueuePage /> },
          { path: "/templates/new", element: <TemplateEditorPage /> },
          { path: "/templates/:templateId/edit", element: <TemplateEditorPage /> },
          { path: "/templates/compose", element: <TemplateComposerPage /> },
          { path: "/templates", element: <TemplateManagerPage /> },
          { path: "/approvals", element: <ApprovalsPage /> },
          { path: "/billing", element: <BillingPage /> },
          { path: "/automations", element: <AutomationsPage /> },
          { path: "/automations/new", element: <WorkflowBuilderPage /> },
          { path: "/automations/:workflowId/edit", element: <WorkflowBuilderPage /> },
          { path: "/inbox", element: <UnifiedInboxPage /> },
          { path: "/marketplace", element: <MarketplacePage /> },
          {
            path: "/agent",
            element: (
              <Suspense fallback={<div className="flex-1 min-h-0 flex items-center justify-center h-full"><div className="agui-shimmer h-4 w-32 rounded-md" /></div>}>
                <AGUIProvider options={{ enableGenerativeUI: false, chatProtocol: "dc-agent" }}>
                  <AGUIDojoChat />
                </AGUIProvider>
              </Suspense>
            ),
          },
          {
            path: "/agent/classic",
            element: (
              <Suspense fallback={<div className="flex-1 min-h-0 flex items-center justify-center h-full"><div className="agui-shimmer h-4 w-32 rounded-md" /></div>}>
                <AGUIProvider options={{ enableGenerativeUI: false }}>
                  <AGUIAgentChat mode="general" />
                </AGUIProvider>
              </Suspense>
            ),
          },
          {
            path: "/agent/legacy",
            element: (
              <Suspense fallback={<div className="flex items-center justify-center h-full">Loading...</div>}>
                <AGUIProvider>
                  <AGUIPostCreation />
                </AGUIProvider>
              </Suspense>
            ),
          },
          { path: "/listening", element: <AgentModeRedirect mode="listening" /> },
          { path: "/ai-images", element: <AgentModeRedirect mode="images" /> },
          { path: "/ai-usage", element: <AgentModeRedirect mode="usage" /> },
          { path: "/orchestrator", element: <OrchestratorPage /> },
          { path: "/orchestrator-v2", element: <Navigate to="/agent" replace /> },
          { path: "/repurpose", element: <AgentModeRedirect mode="repurpose" /> },
          { path: "/help", element: <HelpCenterPage /> },
          { path: "/workspace", element: <WorkspacePage /> },
          { path: "/studio", element: <AgentModeRedirect mode="studio" /> },
          {
            path: "/ops/dead-letters",
            element: (
              <Suspense fallback={lazyShimmer}>
                <DeadLetterQueuePage />
              </Suspense>
            ),
          },
          {
            path: "/dev/tool-coverage",
            element: (
              <Suspense fallback={lazyShimmer}>
                <DevToolCoverageCard />
              </Suspense>
            ),
          },
        ],
      },
    ],
  },
]);

export default function App() {
  const { checkAuth } = useAuthStore();

  // Initialize the 30-minute idle session timeout
  useIdleSession();

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  return (
    <ErrorBoundary>
      <QueryClientProvider client={queryClient}>
        <RouterProvider router={router} />
        <Toaster position="top-right" />
      </QueryClientProvider>
    </ErrorBoundary>
  );
}
